<script setup lang="ts">
import { computed } from "vue";
import useVuelidate from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { useSportactiviteitBestelStore } from "@/stores/sportactiviteit-bestel.store";
import { useProfileStore } from "@/stores/profile.store";
import { Tooltip } from "floating-vue";
import BestelGroepForm from "./BestelGroepForm.vue";
import { scrollToFirstError } from "@/helpers/validation.helper";
import { isStartedSeriesBookable as getIsStartedSeriesBookable } from "@/helpers/activity.helper";

const store = useSportactiviteitBestelStore();
const profileStore = useProfileStore();

const emit = defineEmits<{
    (e: "volgende"): void;
}>();

const toonMezelf = computed(() => {
    if (!profileStore.isLoggedIn) return true;
    const leeftijd = profileStore.profile?.Leeftijd;
    if (leeftijd == null) return true;
    const min = store.activiteit?.MinLeeftijd ?? 0;
    const max = store.activiteit?.MaxLeeftijd ?? 999;
    return leeftijd >= min && leeftijd <= max;
});

const logIn = () => {
    window.location.href = `/umbraco/surface/layout/login?platform=${store.platformId}`;
};

const register = () => {
    window.location.href = `/umbraco/surface/layout/register?platform=${store.platformId}`;
};

const openClaimModal = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('actiecode', 'true');
    window.location.href = url.toString();
};

const aantalDeelnemers = computed(() =>
    store.activiteit?.Groepsactiviteit ? store.aantalGroepen : store.aantalPersonen,
);

const isStartedSeriesBookable = computed(() => {
    return getIsStartedSeriesBookable(
        store.activiteit?.InschrijvenBegonnenReeksMogelijk,
        store.lessen,
    );
});

const isBookingClosedByPastDate = computed(() =>
    store.isGeweest && !isStartedSeriesBookable.value,
);

const rules = computed(() => ({
    aantalDeelnemers: {
        minEenDeelnemer: helpers.withMessage(
            "Voor wie wil je boeken?",
            (val: number) => val > 0,
        ),
        maxDeelnemers: helpers.withMessage(
            () => {
                const n = store.aantalPlaatsenBeschikbaar;
                return n === 1
                    ? `Er is maar ${n} plek beschikbaar`
                    : `Er zijn ${n} plekken beschikbaar`;
            },
            (val: number) =>
                store.aantalPlaatsenBeschikbaar === 0 ||
                val <= store.aantalPlaatsenBeschikbaar,
        ),
        notEnoughCredits: helpers.withMessage(
            "Je hebt niet genoeg credits om voor zoveel personen te boeken.",
            () => !store.notEnoughCredits,
        ),
    },
}));

const v$ = useVuelidate(rules, { aantalDeelnemers });

const validate = async () => v$.value.$validate();

defineExpose({ validate });

const handleVolgende = async () => {
    const valid = await validate();
    if (valid) emit("volgende");
    else scrollToFirstError();
};
</script>

<template>
    <div class="participants-form">

        <!-- Individuele activiteit -->
        <template v-if="!store.activiteit?.Groepsactiviteit">
            <h1 class="participants-form-title h4">Wie gaan er?</h1>

            <!-- Mezelf -->
            <div v-if="toonMezelf" class="participants-form-input">
                <p class="participants-form-input-text">Mezelf</p>
                <div class="participants-form-input-controls">
                    <button type="button" class="btn-minus participants-form-input-controls-buttons"
                        :disabled="!store.boeking.Jezelf.Include || isBookingClosedByPastDate"
                        @click="store.setJezelfInclude(false)" aria-label="Verwijder jezelf">
                        <span class="icon-min--circle-cutout" aria-hidden="true"></span>
                    </button>
                    <p class="participants-form-input-controls-amount">{{ store.boeking.Jezelf.Include ? 1 : 0 }}</p>
                    <button type="button" class="btn-plus participants-form-input-controls-buttons"
                        :disabled="store.boeking.Jezelf.Include || isBookingClosedByPastDate"
                        @click="store.setJezelfInclude(true)" aria-label="Voeg jezelf toe">
                        <span class="icon-add--circle-cutout" aria-hidden="true"></span>
                    </button>
                </div>
            </div>

            <!-- Vriend(in) -->
            <div v-if="(store.activiteit?.MaxLeeftijd ?? 0) >= 18" class="participants-form-input">
                <p class="participants-form-input-text">Vriend(in) (18+)</p>
                <div class="participants-form-input-controls">
                    <button type="button" class="btn-minus participants-form-input-controls-buttons"
                        :disabled="store.boeking.Volwassenen.length === 0 || isBookingClosedByPastDate"
                        @click="store.removeVolwassene()" aria-label="Verwijder een vriend(in)">
                        <span class="icon-min--circle-cutout" aria-hidden="true"></span>
                    </button>
                    <p class="participants-form-input-controls-amount">{{ store.boeking.Volwassenen.length }}</p>
                    <button type="button" class="btn-plus participants-form-input-controls-buttons"
                        :disabled="isBookingClosedByPastDate" @click="store.addVolwassene()" aria-label="Voeg een vriend(in) toe">
                        <span class="icon-add--circle-cutout" aria-hidden="true"></span>
                    </button>
                </div>
            </div>



            <!-- Kind(eren) – gastboeker: disabled met tooltip -->
            <div class="position-relative">
                <Tooltip :triggers="['click', 'hover']" :theme="'payment-info'"
                    v-if="(store.activiteit?.MinLeeftijd ?? 18) < 18 && store.guestAccount">
                    <div class="participants-form-input disabled mb-0">

                        <p class="participants-form-input-text">

                            Kind(eren) (t/m 17 jaar)
                            <span class="icon icon-info" aria-hidden="true"></span>

                        </p>
                        <div class="participants-form-input-controls">
                            <button type="button" class="btn-minus participants-form-input-controls-buttons" disabled
                                aria-label="Verwijder een kind">
                                <span class="icon-min--circle-cutout" aria-hidden="true"></span>
                            </button>
                            <p class="participants-form-input-controls-amount">{{ store.boeking.Kinderen.length }}</p>
                            <button type="button" class="btn-plus participants-form-input-controls-buttons" disabled
                                aria-label="Voeg een kind toe">
                                <span class="icon-add--circle-cutout" aria-hidden="true"></span>
                            </button>
                        </div>
                    </div>
                    <template #popper>
                        Je hebt een account nodig om voor kinderen te boeken.
                    </template>
                </Tooltip>
            </div>

            <!-- Kind(eren) – ingelogd/geen gast -->
            <div v-if="(store.activiteit?.MinLeeftijd ?? 18) < 18 && !store.guestAccount"
                class="participants-form-input mb-0">
                <p class="participants-form-input-text">Kind(eren) (t/m 17 jaar)</p>
                <div class="participants-form-input-controls">
                    <button type="button" class="btn-minus participants-form-input-controls-buttons"
                        :disabled="store.boeking.Kinderen.length === 0 || isBookingClosedByPastDate" @click="store.removeKind()"
                        aria-label="Verwijder een kind">
                        <span class="icon-min--circle-cutout" aria-hidden="true"></span>
                    </button>
                    <p class="participants-form-input-controls-amount">{{ store.boeking.Kinderen.length }}</p>
                    <button type="button" class="btn-plus participants-form-input-controls-buttons"
                        :disabled="isBookingClosedByPastDate" @click="store.addKind()" aria-label="Voeg een kind toe">
                        <span class="icon-add--circle-cutout" aria-hidden="true"></span>
                    </button>
                </div>
            </div>

            <!-- Gastboeker: inloggen / registreren links -->
            <div v-if="store.guestAccount" class="participants-not-logged-in">
                <span>
                    <span class="participants-not-logged-in-login" role="button" @click="logIn">Inloggen</span>
                    <span class="participants-not-logged-in-spacer">of</span>
                    <span class="participants-not-logged-in-make-account" role="button" @click="register">Account
                        aanmaken</span>
                </span>
            </div>

            <!-- Validatieberichten -->
            <div class="mt-2">
                <template v-if="v$.aantalDeelnemers.$dirty">
                    <small v-for="error in v$.aantalDeelnemers.$errors" :key="error.$uid" class="text-danger d-block">{{
                        error.$message }}</small>
                </template>
            </div>

            <!-- Niet genoeg credits -->
            <div v-if="store.notEnoughCredits" class="bg-gray-300 mt-3 p-3">
                <h2 class="h5">Niet genoeg credits</h2>
                <p>Je hebt niet genoeg credits. Heb je nog een actiecode, klik dan op de onderstaande knop om hem te verzilveren.</p>
                <button type="button" class="btn btn-primary" @click="openClaimModal">Verzilver actiecode</button>
            </div>

            <!-- Activiteit verlopen -->
            <div v-if="isBookingClosedByPastDate" class="mt-2">
                <hr class="mt-4 mb-2" />
                <div class="alert alert-secondary" role="alert">
                    Deze activiteit is verlopen, en hierdoor niet meer te boeken.
                </div>
            </div>

            <!-- Desktop stepper -->
            <hr class="payment-stepper-divider" />
            <div class="payment-stepper-desktop d-none d-md-flex">
                <a :href="store.terugUrl" target="_self">
                    <span class="icon-arrow-left mr-1" aria-hidden="true"></span> Terug
                </a>
                <button type="button" class="btn btn-primary" :disabled="isBookingClosedByPastDate" @click="handleVolgende">
                    Volgende
                </button>
            </div>
        </template>

        <!-- Groepsactiviteit -->
        <template v-else>
            <h1 class="participants-form-title h4">Voor wie is deze boeking?</h1>

            <BestelGroepForm
                v-for="(groep, index) in store.boeking.Groepen"
                :key="index"
                :item="groep"
                :index="index"
                :canRemove="store.boeking.Groepen.length > 1"
                @remove="store.removeGroup(index)"
            />

            <!-- Groep toevoegen -->
            <div class="mt-2 mb-3">
                <button
                    type="button"
                    class="btn btn-secondary btn-sm"
                    :disabled="isBookingClosedByPastDate"
                    @click="store.addGroup()"
                >
                    <span class="icon-add mr-1" aria-hidden="true"></span>
                    Groep toevoegen
                </button>
            </div>

            <!-- Activiteit verlopen -->
            <div v-if="isBookingClosedByPastDate" class="mt-2">
                <hr class="mt-4 mb-2" />
                <div class="alert alert-secondary" role="alert">
                    Deze activiteit is verlopen, en hierdoor niet meer te boeken.
                </div>
            </div>

            <!-- Validatieberichten bovenop -->
            <div class="mt-2">
                <template v-if="v$.aantalDeelnemers.$dirty">
                    <small v-for="error in v$.aantalDeelnemers.$errors" :key="error.$uid" class="text-danger d-block">
                        {{ error.$message }}
                    </small>
                </template>
            </div>

            <!-- Niet genoeg credits -->
            <div v-if="store.notEnoughCredits" class="bg-gray-300 mt-3 p-3">
                <h2 class="h5">Niet genoeg credits</h2>
                <p>Je hebt niet genoeg credits. Heb je nog een actiecode, klik dan op de onderstaande knop om hem te verzilveren.</p>
                <button type="button" class="btn btn-primary" @click="openClaimModal">Verzilver actiecode</button>
            </div>

            <!-- Desktop stepper -->
            <hr class="payment-stepper-divider" />
            <div class="payment-stepper-desktop d-none d-md-flex">
                <a :href="store.terugUrl" target="_self">
                    <span class="icon-arrow-left mr-1" aria-hidden="true"></span> Terug
                </a>
                <button type="button" class="btn btn-primary" :disabled="isBookingClosedByPastDate" @click="handleVolgende">
                    Volgende
                </button>
            </div>
        </template>

    </div>
</template>
